import { switchMap, withLatestFrom, filter, mapTo } from 'rxjs/operators'
import { of } from 'rxjs'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import { routes } from '../consts'
import { event, eventTypes } from '../consts/eventConsts'
import {
  getExcludedSubscriptionIds,
  getExcludedDueInvoicesOfSubscriptionsIds
} from '../utils/paymentUtils'

// ******************************** ZUORA PAYMENT FLOW *********************************

export const fetchOrAddPaymentMethod = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.REDIRECTED_TO_CHECKOUT_PAGE),
    withLatestFrom(state$),
    filter(([_, state]) => state.app.features.billing_enabled),
    switchMap(([{ customerCoupon, selectedPlan }, _]) =>
      of(
        Actions.createAction(Actions.GENERATE_PAYMENT_PAGE_DATA_REQUESTED, {
          selectedPlan,
          customerCoupon
        })
      )
    )
  )

export const copayFetchOrAddPaymentMethod = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.COPAY_PLAN_DETAILS_RECEIVED),
    withLatestFrom(state$),
    filter(([_, state]) => state.app.features.billing_enabled),
    switchMap(([_, state]) =>
      of(
        Actions.createAction(Actions.GENERATE_PAYMENT_PAGE_DATA_REQUESTED, {
          customerCoupon: null,
          selectedPlan: state.payment.selectedPlan
        })
      )
    )
  )

export const redirectToPaymentMethodPage = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.GENERATE_PAYMENT_PAGE_DATA_RECEIVED),
    withLatestFrom(state$),
    filter(([_, state]) => state.app.features.billing_enabled),
    filter(([payload, _]) => !payload.hpmCreditCardPaymentMethodId),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.ZUORA_PAYMENT_PAGE))
  )

export const createOrder = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.ADD_PAYMENT_METHOD_SUCCEED),
    withLatestFrom(state$),
    filter(([_, state]) => state.app.features.billing_enabled),
    // filter(
    //   ([{ hpmCreditCardPaymentMethodId }, _]) => hpmCreditCardPaymentMethodId
    // ),
    switchMap(([payload, state]) => {
      const selectedPlan = state.payment.selectedPlan
      const paymentAddress = state.payment.paymentAddressObj
      const billingState = state.payment?.isPaymentFromPPF
        ? state.payment?.ppfUserInfo?.stateCode
        : state.auth.locationVerification.state
        ? state.auth.locationVerification.state
        : paymentAddress.creditCardState
      const customerCoupon = state.payment.customerCoupon
      const userEmail = state.payment?.ppfUserInfo?.email

      if (state.payment.isPaymentFromPPF) {
        return of(
          Actions.createAction(Actions.CREATE_ORDER_REQUESTED_PPF, {
            selectedRatePlanId: selectedPlan?.id,
            selectedRatePlanChargeId: selectedPlan?.charge?.id
              ? selectedPlan?.charge?.id
              : selectedPlan.charges?.[0]?.id,
            billingState,
            customerCoupon: customerCoupon ? customerCoupon : null,
            creditCardAddress1: paymentAddress.creditCardAddress1,
            creditCardAddress2: paymentAddress.creditCardAddress2,
            creditCardCity: paymentAddress.creditCardCity,
            creditCardPostalCode: paymentAddress.creditCardPostalCode,
            creditCardState: paymentAddress.creditCardState,
            username: userEmail,
            ...payload
          })
        )
      } else {
        return of(
          Actions.createAction(Actions.CREATE_ORDER_REQUESTED, {
            selectedRatePlanId: selectedPlan?.id,
            selectedRatePlanChargeId: selectedPlan?.charge?.id
              ? selectedPlan?.charge?.id
              : selectedPlan?.charges?.[0]?.id,
            billingState,
            customerCoupon: customerCoupon ? customerCoupon : null,
            creditCardAddress1: paymentAddress.creditCardAddress1,
            creditCardAddress2: paymentAddress.creditCardAddress2,
            creditCardCity: paymentAddress.creditCardCity,
            creditCardPostalCode: paymentAddress.creditCardPostalCode,
            creditCardState: paymentAddress.creditCardState,
            includeDueInvoices: state.payment.includeDueInvoices,
            excludedDueInvoicesOfSubscriptionsIds:
              getExcludedDueInvoicesOfSubscriptionsIds(
                state.payment.paymentConfigPerSubscriptions
              ),
            excludedSubscriptionIds: getExcludedSubscriptionIds(
              state.payment.paymentConfigPerSubscriptions
            ),
            ...payload
          })
        )
      }
    })
  )

export const orderCreated = (actions$, state$) =>
  actions$.pipe(
    getPayload(Actions.CREATE_ORDER_RECEIVED),
    withLatestFrom(state$),
    filter(([_, state]) => state.app.features.billing_enabled),
    filter(([payload, _]) => !payload.ZuoraRequestError),
    switchMap(([{ response, flow }, state, _]) => {
      if (state.payment.isPaymentFromPPF) {
        let planDetail = state?.payment?.selectedPlan
        let planData = {
          affiliation: 'SOW',
          value: parseFloat(response?.price),
          currency: planDetail?.currency,
          transaction_id: response?.charge_id,
          items: [
            {
              item_id: response?.id,
              item_name: planDetail?.plan_name,
              quantity: 1,
              price: response?.price,
              discount: 0,
              item_list_name: 'PPF'
            }
          ]
        }
        return of(
          Actions.createAction(Actions.PAYMENT_SUCCESS_FROM_PPF),
          Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER),
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            type: eventTypes.purchaseEvent,
            eventName: event.purchaseEvent.purchase,
            data: planData
          })
        )
      } else {
        let discountAmt = 0
        if (response?.coupon_amount !== null) {
          discountAmt = parseFloat(response['coupon_amount'])
        }
        if (response?.coupon_percent !== null) {
          let percent = parseFloat(response['coupon_percent'])
          discountAmt = (percent * parseFloat(response['price'])) / 100
        }
        let planDetail = state?.payment?.selectedPlan
        //Math.abs() to make any negative value to positive
        let planData = {
          affiliation: 'SOW',
          value: parseFloat(response?.price) - Math.abs(discountAmt),
          currency: planDetail?.currency,
          transaction_id: response?.charge_id,
          items: [
            {
              item_id: response?.id,
              item_name: planDetail?.plan_name,
              quantity: 1,
              price: response?.price,
              discount: Math.abs(discountAmt),
              item_list_name: 'Not PPF'
            }
          ]
        }
        if (response?.coupon !== null) {
          planData = { ...planData, coupon: response?.coupon }
        }
        if (flow === 'hmo-order') {
          return of(
            Actions.createAction(Actions.PAYMENT_SUCCESS),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              type: eventTypes.purchaseEvent,
              eventName: event.purchaseEvent.purchase,
              data: planData
            }),
            Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER)
          )
        }
        return of(
          Actions.createAction(Actions.PAYMENT_SUCCESS),
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            type: eventTypes.purchaseEvent,
            eventName: event.purchaseEvent.purchase,
            data: planData
          }),
          Actions.createAction(Actions.DISABLE_ZOURA_PAGE_LOADER),
          Actions.createAction(Actions.USER_PROFILE_REQUESTED)
        )
      }
    })
  )
